/**
 * Permissions a person cannot give when they build or edit a role.
 *
 * The API says which ones (GET /permissions?account_id= marks each item
 * `grantable` with a `not_grantable_reason`); the role editors only show it,
 * with the shared locked look (../base/Locked): an amber lock instead of the
 * checkbox, a "Can't give" / "Staff only" pill, and the reason written on
 * screen — not in a tooltip, which a phone or iPad never shows.
 */
import React from 'react';
import { LockedNotice } from '../base/Locked';

/** Why a permission cannot be given (the API's `not_grantable_reason`). */
export type PermissionLockReason = 'not_held' | 'staff_only';

/** permission key → why the current user cannot give it. */
export type PermissionLockReasons = Record<string, PermissionLockReason | null | undefined>;

export const PERMISSION_LOCK_TEXT: Record<PermissionLockReason, string> = {
  not_held: "You don't have this permission, so you can't give it.",
  staff_only: 'Only TURN-CONNECT staff can give this.',
};

/** The pill beside a locked permission's name. */
export const PERMISSION_LOCK_BADGE: Record<PermissionLockReason, string> = {
  not_held: "Can't give",
  staff_only: 'Staff only',
};

const isStaffOnly = (reason?: string | null) => reason === 'staff_only';

/** The line under a locked permission. Unknown or missing reasons read as "not held". */
export function permissionLockText(reason?: string | null): string {
  return isStaffOnly(reason) ? PERMISSION_LOCK_TEXT.staff_only : PERMISSION_LOCK_TEXT.not_held;
}

/** The pill text for a locked permission. */
export function permissionLockBadge(reason?: string | null): string {
  return isStaffOnly(reason) ? PERMISSION_LOCK_BADGE.staff_only : PERMISSION_LOCK_BADGE.not_held;
}

/** Who to ask, after the reason opened by a tap. */
export function permissionLockHelp(reason?: string | null): string {
  return isStaffOnly(reason) ? 'Ask TURN-CONNECT support if you need it.' : 'Ask an account administrator if you need it.';
}

/**
 * The line under a permission you can't give that the role already had when
 * the editor opened: it stays a real checkbox (untick, tick again) until saved.
 * `onRole` is whether it is ticked right now.
 */
export function permissionKeptText(reason: string | null | undefined, onRole: boolean): string {
  const addBack = isStaffOnly(reason) ? 'only TURN-CONNECT staff can add it back' : "you can't add it back";
  return onRole
    ? `Already on this role. You can remove it, but ${addBack} later.`
    : `Was on this role. Tick it again to keep it. Once saved, ${addBack}.`;
}

/** The notice above a role editor that has locked permissions (its plain text). */
export const LOCKED_PERMISSIONS_NOTICE = "Permissions with a lock are ones you can't give. Ask an account administrator if you need them.";

/** The notice, beside the same lock the locked permissions carry. It names no
 * pill: Staff only rows have their own pill and reason. */
export const PermissionLocksNotice: React.FC<{ testId?: string; action?: React.ReactNode; style?: React.CSSProperties }> = ({
  testId,
  action,
  style,
}) => (
  <LockedNotice testId={testId} action={action} style={style}>
    {LOCKED_PERMISSIONS_NOTICE}
  </LockedNotice>
);
